import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../store/auth';
import { useLiveEvents } from '../hooks/useLiveEvents';
import BackBtn from '../components/BackBtn';

export default function Screenshots() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [shots, setShots] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState('screenshot');
  const [tab, setTab] = useState('screen');  // 'screen' | 'camera' | 'recording'
  const [stitching, setStitching] = useState(false);

  const load = async () => {
    if (tab === 'recording') {
      try {
        const { data } = await api.get(`/devices/${deviceId}/recordings`);
        setRecordings(data);
      } catch { setRecordings([]); }
      return;
    }
    const isCamera = tab === 'camera';
    const { data } = await api.get(`/devices/${deviceId}/screenshots?limit=100&is_camera=${isCamera}`);
    setShots(data);
  };

  useEffect(() => { load(); }, [deviceId, tab]);

  useLiveEvents((e) => {
    if (e.device_id !== deviceId) return;
    if (e.event === 'screenshot') {
      const isCamera = e.is_camera;
      if ((isCamera && tab === 'camera') || (!isCamera && tab === 'screen')) {
        setShots(prev => [{ id: Date.now(), file_path: e.path, captured_at: e.ts }, ...prev]);
      }
    }
    if (e.event === 'recording_started') setStitching(false);
    if (e.event === 'recording_stopped') setStitching(true);
    if (e.event === 'recording_ready') {
      setStitching(false);
      if (tab === 'recording') load();
      else setRecordings(prev => [{
        filename: e.filename, recorded_at: e.ts, size_bytes: 0,
      }, ...prev]);
    }
  });

  const token = localStorage.getItem('token');

  const imgUrl = (item) => {
    const folder = tab === 'camera' ? 'camera' : 'screenshot';
    const fname = item.file_path?.split(/[\\/]/).pop();
    return `/files/${folder}/${fname}?token=${token}`;
  };

  const recUrl = (filename) => `/files/recording/${filename}?token=${token}`;

  const deleteShot = async (e, shot) => {
    e.stopPropagation();
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/devices/${deviceId}/screenshots/${shot.id}`);
      setShots(prev => prev.filter(s => s.id !== shot.id));
      if (selected?.id === shot.id) setSelected(null);
    } catch { alert('Delete failed'); }
  };

  const deleteRecording = async (e, rec) => {
    e.stopPropagation();
    if (!window.confirm('Delete this recording?')) return;
    try {
      await api.delete(`/devices/${deviceId}/recordings/${rec.filename}`);
      setRecordings(prev => prev.filter(r => r.filename !== rec.filename));
      if (selected?.filename === rec.filename) setSelected(null);
    } catch { alert('Delete failed'); }
  };

  const deleteAll = async () => {
    const label = tab === 'camera' ? 'camera captures' : 'screenshots';
    if (!window.confirm(`Delete ALL ${label} for this device?`)) return;
    try {
      await api.delete(`/devices/${deviceId}/screenshots?is_camera=${tab === 'camera'}`);
      setShots([]);
      setSelected(null);
    } catch { alert('Delete failed'); }
  };

  const title = tab === 'camera' ? 'Webcam Captures' : tab === 'recording' ? 'Recordings' : 'Screenshots';

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <BackBtn onClick={() => navigate(`/device/${deviceId}`)} />
        <h2 style={{ fontWeight:700 }}>{title}</h2>
        <div style={{ display:'flex', gap:4 }}>
          {[['screen','Screen'],['camera','Camera'],['recording','Recordings']].map(([t, label]) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding:'4px 12px', border:'1px solid var(--border)', borderRadius:6,
              background: tab === t ? 'var(--accent)' : 'transparent',
              color: tab === t ? '#fff' : 'var(--muted)', cursor:'pointer', fontSize:12,
            }}>{label}</button>
          ))}
        </div>
        <button onClick={load} style={btnStyle}>Refresh</button>
        {tab !== 'recording' && (
          <button onClick={deleteAll} style={{ ...btnStyle, color:'#ef4444', borderColor:'#ef444444' }}>
            Delete All
          </button>
        )}
        {stitching && (
          <span style={{ fontSize:12, color:'#f59e0b' }}>⏳ Stitching video…</span>
        )}
        <span style={{ color:'var(--muted)', fontSize:12 }}>
          {tab === 'recording' ? recordings.length : shots.length} items
        </span>
      </div>

      {/* ── Recording tab: video player grid ── */}
      {tab === 'recording' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
          {recordings.map((rec, i) => (
            <div key={i} style={{ border:'1px solid var(--border)', borderRadius:8,
              overflow:'hidden', background:'var(--surface)' }}>
              <video
                src={recUrl(rec.filename)}
                controls
                style={{ width:'100%', display:'block', background:'#000' }}
              />
              <div style={{ padding:'8px 10px', display:'flex', justifyContent:'space-between',
                alignItems:'center', gap:8 }}>
                <div>
                  <div style={{ fontSize:12, color:'var(--text)' }}>
                    {rec.recorded_at ? new Date(rec.recorded_at + 'Z').toLocaleString() : rec.filename}
                  </div>
                  {rec.size_bytes > 0 && (
                    <div style={{ fontSize:11, color:'var(--muted)' }}>
                      {(rec.size_bytes / 1024 / 1024).toFixed(1)} MB
                    </div>
                  )}
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <a href={recUrl(rec.filename)} download={rec.filename}
                    style={{ ...btnSmall, textDecoration:'none', textAlign:'center' }}>↓</a>
                  <button onClick={(e) => deleteRecording(e, rec)}
                    style={{ ...btnSmall, color:'#ef4444', borderColor:'#ef444444' }}>✕</button>
                </div>
              </div>
            </div>
          ))}
          {recordings.length === 0 && !stitching && (
            <p style={{ color:'var(--muted)', padding:40 }}>
              No recordings yet. Use the Record button on the device page.
            </p>
          )}
        </div>
      )}

      {/* ── Screen / Camera tab: square thumbnail grid ── */}
      {tab !== 'recording' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12 }}>
          {shots.map((s, i) => (
            <div key={i} style={{ border:'1px solid var(--border)', borderRadius:8,
              overflow:'hidden', background:'var(--surface)' }}>
              <div onClick={() => { setSelected(s); setSelectedFolder(tab === 'camera' ? 'camera' : 'screenshot'); }}
                style={{ cursor:'pointer' }}>
                {/* Square thumbnail */}
                <div style={{ position:'relative', width:'100%', paddingBottom:'100%',
                  overflow:'hidden', background:'#111' }}>
                  <img src={imgUrl(s)} alt=""
                    style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%',
                      objectFit:'cover', display:'block' }}
                    onError={e => { e.target.style.opacity = '0'; }} />
                </div>
                <div style={{ padding:'5px 8px', fontSize:10, color:'var(--muted)',
                  whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {new Date((s.captured_at||'')+'Z').toLocaleString()}
                  {s.triggered && <span style={{ color:'var(--accent)', marginLeft:4 }}>[triggered]</span>}
                </div>
              </div>
              <div style={{ display:'flex', gap:4, padding:'0 8px 8px' }}>
                <a href={imgUrl(s)} download
                  style={{ ...btnSmall, textDecoration:'none', textAlign:'center', flex:1 }}
                  onClick={e => e.stopPropagation()}>↓</a>
                <button onClick={(e) => deleteShot(e, s)}
                  style={{ ...btnSmall, color:'#ef4444', borderColor:'#ef444444', flex:1 }}>✕</button>
              </div>
            </div>
          ))}
          {shots.length === 0 && (
            <p style={{ color:'var(--muted)', padding:40 }}>No captures yet.</p>
          )}
        </div>
      )}

      {/* ── Lightbox for screen/camera ── */}
      {selected && tab !== 'recording' && (
        <div onClick={() => setSelected(null)} style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,.92)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:100,
        }}>
          <div onClick={e => e.stopPropagation()} style={{ position:'relative' }}>
            <img src={`/files/${selectedFolder}/${selected.file_path?.split(/[\\/]/).pop()}?token=${token}`}
              alt="" style={{ maxWidth:'95vw', maxHeight:'90vh', borderRadius:8, display:'block' }} />
            <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:10 }}>
              <a href={`/files/${selectedFolder}/${selected.file_path?.split(/[\\/]/).pop()}?token=${token}`}
                download style={{ ...btnStyle, textDecoration:'none', color:'#fff', background:'#ffffff22' }}>
                ↓ Download
              </a>
              <button onClick={(e) => { deleteShot(e, selected); setSelected(null); }}
                style={{ ...btnStyle, color:'#ef4444' }}>Delete</button>
              <button onClick={() => setSelected(null)}
                style={{ ...btnStyle, color:'var(--muted)' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btnStyle = {
  padding:'6px 14px', background:'transparent', border:'1px solid var(--border)',
  borderRadius:6, color:'var(--muted)', cursor:'pointer',
};
const btnSmall = {
  padding:'3px 8px', background:'transparent', border:'1px solid var(--border)',
  borderRadius:4, color:'var(--muted)', cursor:'pointer', fontSize:11,
};
